<template>
  <div class="card">
    <div class="card-header">
      <h3 class="card-title">
        Will Call Rides
      </h3>
    </div>
    <hr class="card-title-line">
    <div
      v-loading="isLoading"
      class="card-content"
    >
      <div class="row align-items-center mb-10">
        <span class="col-sm-2 control-label">Filter:</span>
        <div class="col-sm-8 filter-button-group d-flex align-items-center flex-wrap">
          <el-radio-group v-model="selectedFilter">
            <el-radio-button label="unused">
              Unused
            </el-radio-button>
            <el-radio-button label="used">
              Used
            </el-radio-button>
            <el-radio-button label="all">
              All
            </el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <div
        v-loading="approvedRidesLoading"
        class="approved-rides-table-wrap mb-20"
      >
        <table-view-container
          :page-id="pageId"
          :data="rows"
          :table-columns="tableColumns"
          :selected-index="selectedIndex"
          :height="280"
        >
          <template #right-fixed-columns>
            <el-table-column
              v-if="!isDisabled"
              fixed="right"
              label="Action"
              width="50"
            >
              <template #default="scope">
                <div
                  v-if="!scope.row.canceledAt && !scope.row.completedAt && !scope.row.executedAt"
                  class="info-icon"
                  title="Copy to form"
                  @click="onCopyToForm(scope.row)"
                >
                  <i class="fa fa-copy" />
                </div>
              </template>
            </el-table-column>
          </template>
        </table-view-container>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import util from '@/Utils/util';
import { fetchWillCallRideRecords } from '@/services/scheduledRide/fetchWillCallRideRecords';
import { TableColumnType } from '@/models/table';
import type { ScheduledRide } from '@/models/scheduledRide';

const pageId = 'will-call-rides-for-order';

function formatAddress(row: Record<string, unknown>, kind: 'start' | 'end') {
  const num = kind === 'start' ? '1' : '2';
  const parts = [row[`streetAddress${num}`], row[`city${num}`], row[`state${num}`], row[`zip${num}`]].filter(Boolean);
  return parts.join(', ') || '—';
}

const tableColumns: TableColumnType[] = [
  { label: 'ID', field: 'id', width: 60 },
  { label: 'Pickup Date', field: 'pickupDate', width: 110 },
  { label: 'Pickup', field: 'pickupAddress', minWidth: 200 },
  { label: 'Dropoff', field: 'dropoffAddress', minWidth: 200 },
  { label: 'Status', field: 'statusLabel', width: 90 },
];

const props = withDefaults(
  defineProps<{
    isLoading: boolean;
    phoneNumber: string;
    isDisabled: boolean;
    requestId: string | null;
    selectedWillCallRideId: number | null;
  }>(),
  {
    isLoading: false,
    phoneNumber: '',
    requestId: null,
    isDisabled: false,
    selectedWillCallRideId: null,
  },
);

const emit = defineEmits<{
  (e: 'copy-to-form', row: ScheduledRide): void;
}>();

const selectedFilter = ref<'used' | 'unused' | 'all'>('unused');
const allRows = ref<(ScheduledRide & { pickupAddress: string; dropoffAddress: string; statusLabel: string })[]>([]);
const approvedRidesLoading = ref(false);

const rows = computed(() => {
  if (selectedFilter.value === 'all') {
    return allRows.value;
  }

  return allRows.value.filter((row) => {
    const used = !!row.completedAt || !!row.executedAt;
    const canceled = !!row.canceledAt;
    const unused = !used && !canceled;

    if (selectedFilter.value === 'unused') {
      return unused;
    }

    return used;
  });
});

const selectedIndex = computed(() => rows.value.findIndex((row) => Number(row.id) === props.selectedWillCallRideId));

async function loadApprovedRides() {
  const phoneNumber = props.phoneNumber && util.getValidPhoneNumber(props.phoneNumber);
  if (!phoneNumber) {
    allRows.value = [];
    return;
  }

  approvedRidesLoading.value = true;
  try {
    const res = await fetchWillCallRideRecords({
      phoneNumber,
    });
    const rows = res?.rows ?? [];
    allRows.value = rows.map((r) => ({
      ...r,
      pickupAddress: formatAddress(r, 'start'),
      dropoffAddress: formatAddress(r, 'end'),
      statusLabel: r.canceledAt ? 'Canceled' : r.completedAt || r.executedAt ? 'Used' : 'Unused',
    }));
  } catch {
    allRows.value = [];
  }
  approvedRidesLoading.value = false;
}

function onCopyToForm(row: ScheduledRide) {
  emit('copy-to-form', row);
}

watch(
  () => [props.phoneNumber, props.requestId],
  ([phoneNumber]) => {
    if (phoneNumber && util.getValidPhoneNumber(phoneNumber)) {
      loadApprovedRides();
    } else {
      allRows.value = [];
    }
  },
  { immediate: true },
);

defineExpose({
  loadApprovedRides,
});
</script>
